import type { RichLead } from '../lead.popup/types';
import type { RootScope } from '../types';

import $ from 'jquery';

import { type FavoriteUnitRef, opportunityApi } from '../services/api';
import { logger } from '../services/logger';
import { getSharedWorkspaceContext } from '../services/sharedWorkspace';
import { getCurrentLead } from '../unit.popup/common';

let resolveFavorites!: (units: FavoriteUnitRef[]) => void;
export const workspaceFavoritesReady = new Promise<FavoriteUnitRef[]>(
  (resolve) => {
    resolveFavorites = resolve;
  },
);

export class UnitFavoriteBtnCtrl {
  isAvailable = false;
  isPending = false;

  /**
   * A storage for favorite units in shared segments feature
   */
  favoriteUnits: { [unitId: string]: FavoriteUnitRef | undefined } = {};

  static $inject = ['$scope', '$element', '$rootScope'];

  constructor(
    private $scope: angular.IScope,
    private $element: angular.IAugmentedJQuery,
    private $rootScope: RootScope,
  ) {
    this.isAvailable =
      !!(
        $rootScope.workingWithActive &&
        $rootScope.leadHasActiveOpportunity &&
        getCurrentLead()?.id
      ) || !!getSharedWorkspaceContext();

    this.fetchWorkspaceData();

    if ($rootScope.user_id) {
      $rootScope.$on(
        'proposal.favoriteUnits.changed',
        (_e, data: { leadId: RichLead['id'] }) => {
          const leadId = getCurrentLead()?.id ?? null;

          // TODO: do we need this check?
          if (data?.leadId !== leadId) {
            return;
          }

          // TODO: check whether we can forward data in event instead of fetching here
          this.fetchWorkspaceData();
        },
      );
    }
  }

  private async fetchWorkspaceData(): Promise<void> {
    const sharedCtx = getSharedWorkspaceContext();
    const leadId = getCurrentLead()?.id ?? null;

    try {
      let units: FavoriteUnitRef[] = [];

      if (sharedCtx) {
        // Inits Unit grid in public side (access by clients and managers)
        // See: application/views/sections/section_production/_public.php
        const data = await opportunityApi.getWorkspaceUnits({
          opportunityId: sharedCtx.opportunityId,
          urlToken: sharedCtx.urlToken,
        });

        units = data.favorite_units;
      } else if (!leadId) {
        resolveFavorites([]);
      } else if (this.$rootScope.user_id) {
        // Inits Unit grid in private side (access only by managers)
        // See: application/views/sections/section_production/_private.php
        units = await opportunityApi.getFavoriteUnits(leadId);
      }

      this.favoriteUnits = Object.fromEntries(
        units.map((ref) => [ref.id, ref]),
      );
      this.$rootScope.$applyAsync();

      resolveFavorites(units);
      this.$rootScope.$broadcast('favoriteUnitsUpdated', { units });
    } catch (error) {
      logger.error('Error fetching workspace and favorites data', {
        error,
        sharedCtx,
        leadId,
      });

      resolveFavorites([]);
    }
  }

  isInFavorites = (unitId: string) => !!this.favoriteUnits[unitId];

  isLikedByClient = (unitId: string) =>
    this.favoriteUnits[unitId]?.origin === opportunityApi.ORIGIN.CLIENT;

  isLikedByManager = (unitId: string) =>
    this.favoriteUnits[unitId]?.origin === opportunityApi.ORIGIN.MANAGER;

  isDisabled = (unitId: string) => {
    if (this.$rootScope.user_id && this.isLikedByClient(unitId)) {
      // if was favored by client — don't touch it anymore

      return true;
    }

    return isProposalLockedFromDOM(unitId);
  };

  onToggleFavorite = (event: Event): void => {
    const btn = event.currentTarget;

    if (!(btn instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const { unitId } = btn.dataset;

    if (!unitId) {
      return;
    }

    if (this.isPending || !unitId) {
      return;
    }

    this.toggleFavorite({ unitId });
  };

  private async toggleFavorite({ unitId }: { unitId: string }): Promise<void> {
    const favState = this.favoriteUnits[unitId];

    this.isPending = true;
    this.$scope.$applyAsync();

    const sharedCtx = getSharedWorkspaceContext();
    const leadId = sharedCtx?.leadId || getCurrentLead()?.id || null;

    if (this.isDisabled(unitId) && !favState) {
      // the unit is not available for sell and can not be favored
      this.isPending = false;
      this.$scope.$applyAsync();

      return;
    }

    try {
      if (this.$rootScope.user_id) {
        if (!leadId) {
          this.isPending = false;
          this.$scope.$applyAsync();

          return;
        }

        await (favState
          ? opportunityApi.removeFavoriteUnit({
              leadId,
              unitId,
            })
          : opportunityApi.addFavoriteUnit({
              leadId,
              unitId,
            }));
      } else {
        if (!sharedCtx) {
          this.isPending = false;
          this.$scope.$applyAsync();

          return;
        }

        await (favState
          ? opportunityApi.removeFavoriteUnitForVisitor({
              opportunityId: sharedCtx.opportunityId,
              urlToken: sharedCtx.urlToken,
              unitId,
            })
          : opportunityApi.addFavoriteUnitForVisitor({
              opportunityId: sharedCtx.opportunityId,
              urlToken: sharedCtx.urlToken,
              unitId,
            }));
      }

      await this.fetchWorkspaceData();

      if (leadId) {
        // TODO: remove or move into fetchWorkspaceData?
        this.$rootScope.$broadcast('proposal.favoriteUnits.changed', {
          leadId,
        });
      }

      this.isPending = false;
      this.$scope.$applyAsync();
    } catch (error) {
      logger.error('Error toggling favorite', {
        error,
        sharedCtx,
        unitId,
      });
    } finally {
      this.isPending = false;
      this.$scope.$applyAsync();
    }
  }
}

/**
 * This is a hack to get unit state from DOM
 * We have a `unit.status.changed` listener in app.js that updates classes depending on unit status
 */
export function isProposalLockedFromDOM(unitId: string): boolean {
  const element = $(`div[data-unit-id="${unitId}"]`);

  return (
    (element.hasClass('-type_notinstock') || element.hasClass('-type_sold')) &&
    !element.hasClass('-type_resale')
  );
}
